import { useStorage } from "@platform/hooks";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type {
  ColumnEventType,
  ColumnVisibleEvent,
  GridApi,
} from "ag-grid-community";

interface ColumnVisibilityChange {
  readonly colId: string;
  readonly visible: boolean;
}

// AG Grid sets these itself while it loads column definitions; they say
// nothing about what the user chose to hide.
const GRID_DRIVEN_SOURCES: ReadonlySet<ColumnEventType> = new Set([
  "gridInitializing",
  "gridOptionsChanged",
]);

export const quotesHiddenColumnsStorageKey = (ecoRfpId: string): string =>
  `quotes-grid-hidden-columns:${ecoRfpId}`;

/**
 * Applies a visibility change to the stored hidden column IDs. Only the
 * changed columns are touched: the grid shows one coverage at a time, so the
 * stored list also holds hidden columns of coverages not on screen.
 */
export const mergeHiddenColumnIds = (
  storedHiddenIds: readonly string[],
  changes: readonly ColumnVisibilityChange[],
): string[] => {
  const changedIds = new Set(changes.map((change) => change.colId));
  return [
    ...storedHiddenIds.filter((colId) => !changedIds.has(colId)),
    ...changes
      .filter((change) => !change.visible)
      .map((change) => change.colId),
  ];
};

/**
 * Says which plan columns of the rates and premiums grids to show, given the
 * benefits grid's columns. Those grids have one column per plan (`{planId}`)
 * where the benefits grid can have one per network (`{planId}.inNetwork`), so
 * AG Grid's aligned grids, which match columns by ID, can't carry a hide
 * across. A plan's column shows while any of its benefits columns does.
 * Columns with no benefits counterpart are left out.
 */
export const companionColumnVisibility = (
  benefitsColumns: readonly ColumnVisibilityChange[],
  companionColIds: readonly string[],
): ColumnVisibilityChange[] =>
  companionColIds.flatMap((colId) => {
    const matches = benefitsColumns.filter(
      (column) =>
        column.colId === colId || column.colId.startsWith(`${colId}.`),
    );
    return matches.length > 0
      ? [{ colId, visible: matches.some((column) => column.visible) }]
      : [];
  });

interface QuotesHiddenColumns {
  /** Call with the benefits (plan editor) grid's API once it is ready. */
  readonly trackPlanEditorGrid: (api: GridApi) => void;
  /** Call with the rates or premiums grid's API once it is ready. */
  readonly trackCompanionGrid: (api: GridApi) => void;
}

/**
 * Keeps the plan columns a user hides in the Quotes benefits grid hidden
 * across page loads, per RFP, in the browser's local storage, and hides the
 * same plans in the rates and premiums grids below it.
 *
 * The grid's mpx-backed state (`useQuotesGridStatePersistence`) restores only
 * once, when the grid mounts, while the plan columns change with every
 * coverage the user opens. So the hidden columns are re-applied whenever the
 * grid loads a new set of columns.
 */
export const useQuotesHiddenColumns = (
  ecoRfpId: string,
): QuotesHiddenColumns => {
  const { getStorageItem, setStorageItem } = useStorage("local");
  // Read at event time so the returned callbacks stay stable: a new
  // onGridReady makes PlanEditor rebuild its grid options.
  const ecoRfpIdRef = useRef(ecoRfpId);
  ecoRfpIdRef.current = ecoRfpId;
  const planEditorApiRef = useRef<GridApi | null>(null);
  const companionApisRef = useRef(new Set<GridApi>());
  const detachRef = useRef<(() => void) | null>(null);

  useEffect(() => () => detachRef.current?.(), []);

  const syncCompanionGrid = useCallback((companionApi: GridApi): void => {
    const planEditorApi = planEditorApiRef.current;
    if (!planEditorApi || planEditorApi.isDestroyed()) return;
    const benefitsColumns = (planEditorApi.getColumns() ?? []).map(
      (column) => ({ colId: column.getColId(), visible: column.isVisible() }),
    );
    const changes = companionColumnVisibility(
      benefitsColumns,
      (companionApi.getColumns() ?? []).map((column) => column.getColId()),
    ).filter(
      ({ colId, visible }) =>
        companionApi.getColumn(colId)?.isVisible() !== visible,
    );
    for (const visible of [true, false]) {
      const colIds = changes
        .filter((change) => change.visible === visible)
        .map((change) => change.colId);
      if (colIds.length > 0) companionApi.setColumnsVisible(colIds, visible);
    }
  }, []);

  const syncCompanionGrids = useCallback((): void => {
    for (const companionApi of companionApisRef.current) {
      if (companionApi.isDestroyed()) {
        companionApisRef.current.delete(companionApi);
      } else {
        syncCompanionGrid(companionApi);
      }
    }
  }, [syncCompanionGrid]);

  const trackPlanEditorGrid = useCallback(
    (api: GridApi) => {
      detachRef.current?.();
      planEditorApiRef.current = api;

      const readHiddenIds = (): string[] =>
        ecoRfpIdRef.current
          ? (getStorageItem<string[]>(
              quotesHiddenColumnsStorageKey(ecoRfpIdRef.current),
            ) ?? [])
          : [];

      const applyHiddenColumns = (): void => {
        const hiddenIdsShowing = readHiddenIds().filter(
          (colId) => api.getColumn(colId)?.isVisible() === true,
        );
        if (hiddenIdsShowing.length > 0) {
          api.setColumnsVisible(hiddenIdsShowing, false);
        }
        syncCompanionGrids();
      };

      const handleVisibilityChange = (event: ColumnVisibleEvent): void => {
        syncCompanionGrids();
        const isUserChange =
          event.columns !== null && !GRID_DRIVEN_SOURCES.has(event.source);
        if (!ecoRfpIdRef.current || !isUserChange) return;

        const changes = (event.columns ?? []).map((column) => ({
          colId: column.getColId(),
          visible: column.isVisible(),
        }));
        setStorageItem(
          quotesHiddenColumnsStorageKey(ecoRfpIdRef.current),
          mergeHiddenColumnIds(readHiddenIds(), changes),
        );
      };

      applyHiddenColumns();
      api.addEventListener("newColumnsLoaded", applyHiddenColumns);
      api.addEventListener("columnVisible", handleVisibilityChange);
      detachRef.current = () => {
        if (api.isDestroyed()) return;
        api.removeEventListener("newColumnsLoaded", applyHiddenColumns);
        api.removeEventListener("columnVisible", handleVisibilityChange);
      };
    },
    [getStorageItem, setStorageItem, syncCompanionGrids],
  );

  const trackCompanionGrid = useCallback(
    (api: GridApi) => {
      if (companionApisRef.current.has(api)) return;
      companionApisRef.current.add(api);
      // The rates and premiums grids load their plan columns on their own
      // schedule, after or before the benefits grid's.
      api.addEventListener("newColumnsLoaded", () => syncCompanionGrid(api));
      syncCompanionGrid(api);
    },
    [syncCompanionGrid],
  );

  return useMemo(
    () => ({ trackPlanEditorGrid, trackCompanionGrid }),
    [trackPlanEditorGrid, trackCompanionGrid],
  );
};
